import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowLeft,
  Calendar,
  Car,
  Loader,
  MapPin,
  Navigation,
  Phone,
  Save,
  Search,
  Settings,
  User,
  X,
  AlertCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
  Polyline,
} from "react-leaflet";
import { Link, useNavigate } from "react-router-dom";
import polyline from "@mapbox/polyline";
import { api, API_BASE } from "../../utils/api";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const fromIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const toIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const currentLocationIcon = new L.DivIcon({
  html: `
    <div style="font-size: 28px; line-height: 28px; transform: translate(-50%, -100%);">
      📍
    </div>
  `,
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const FEATURES_OPTIONS = [
  { value: "hill_start_assist", label: "Hill Start Assist" },
  { value: "esc", label: "ESC (Electronic Stability Control)" },
  { value: "aeb", label: "AEB (Auto Emergency Braking)" },
  { value: "acc", label: "ACC (Adaptive Cruise Control)" },
  { value: "tcs", label: "TCS (Traction Control System)" },
  { value: "abs", label: "ABS (Anti-lock Braking System)" },
  { value: "adas", label: "ADAS (Advanced Driver Assistance)" },
  { value: "lane_keep_assist", label: "Lane Keep Assist" },
  { value: "hill_descent_control", label: "Hill Descent Control" },
  { value: "lane_departure_warning", label: "Lane Departure Warning" },
  { value: "blind_spot_monitor", label: "Blind Spot Monitor" },
  { value: "drive_modes", label: "Drive Modes" },
];

const TRANSMISSION_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "automatic", label: "Automatic" },
];

const AUTOMATIC_SUB_OPTIONS = [
  { value: "amt", label: "AMT (Automated Manual)" },
  { value: "cvt", label: "CVT (Continuously Variable)" },
  { value: "dct", label: "DCT (Dual Clutch)" },
];

const VEHICLE_TYPE_OPTIONS = [
  { value: "hatchback", label: "Hatchback" },
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV" },
  { value: "muv", label: "MUV" },
  { value: "bike", label: "Bike" },
];

const FUEL_TYPE_OPTIONS = [
  { value: "petrol", label: "Petrol" },
  { value: "diesel", label: "Diesel" },
  { value: "electric", label: "Electric" },
  { value: "hybrid", label: "Hybrid" },
  { value: "cng", label: "CNG" },
];

function FlyToLocation({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 15, { duration: 1.2 });
    }
  }, [position, map]);
  return null;
}

function MapPinHandler({ onMapClick, activePinField }) {
  const map = useMapEvents({
    click(e) {
      onMapClick(e.latlng, activePinField);
    },
  });
  return null;
}

async function geocodeAddress(address) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`;
  const res = await fetch(url, { headers: { "Accept-Language": "en" } });
  const data = await res.json();
  if (data && data.length > 0) {
    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
      name: data[0].display_name,
    };
  }
  return null;
}

async function fetchSuggestions(query) {
  if (!query || query.trim().length < 2) return [];
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`;
  const res = await fetch(url, { headers: { "Accept-Language": "en" } });
  const data = await res.json();
  return data.map((item) => ({
    name: item.display_name,
    lat: parseFloat(item.lat),
    lng: parseFloat(item.lon),
  }));
}

// Reverse geocode coordinates
/* async function reverseGeocode(lat, lng) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
  const res = await fetch(url, { headers: { "Accept-Language": "en" } });
  const data = await res.json();
  return data?.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
} */

// Reverse geocode coordinates using VMmaps
async function reverseGeocode(lat, lng){

  const token = localStorage.getItem("token");

  const res = await fetch(`${API_BASE}/geocoding/reverse`,{
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      latitude: lat,
      longitude: lng
    })
  });

  const data = await res.json();

  return( data?.placeName || `${lat.toFixed(5)},${lng.toFixed(5)}`);
}

const CreateTrip = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get current datetime string for date restrictions (called as a function to always return current time)
  const getNow = () => {
    const now = new Date();
    // Format to YYYY-MM-DDTHH:MM (datetime-local format)
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  // Map state
  const [fromPosition, setFromPosition] = useState(null);
  const [fromLabel, setFromLabel] = useState("");
  const [toPosition, setToPosition] = useState(null);
  const [toLabel, setToLabel] = useState("");
  const [stops, setStops] = useState([]); // Array of { lat, lng, name }
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
  const [activePinField, setActivePinField] = useState(null); 
  const [gettingLocation, setGettingLocation] = useState(false);
  const [searchingFrom, setSearchingFrom] = useState(false);
  const [searchingTo, setSearchingTo] = useState(false);
  const [searchingStop, setSearchingStop] = useState(false);
  const [routePoints, setRoutePoints] = useState([]);
  const [estimatedDistance, setEstimatedDistance] = useState(0);
  const [estimatedDuration, setEstimatedDuration] = useState(0);
  const [showStopsUI, setShowStopsUI] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);

  // Search text + autocomplete suggestions
  const [fromSearchText, setFromSearchText] = useState("");
  const [toSearchText, setToSearchText] = useState("");
  const [stopSearchText, setStopSearchText] = useState("");
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [stopSuggestions, setStopSuggestions] = useState([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [showStopSuggestions, setShowStopSuggestions] = useState(false);
  const fromDebounceRef = useRef(null);
  const toDebounceRef = useRef(null);
  const stopDebounceRef = useRef(null);

  useEffect(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const latlng = [latitude, longitude];
        setCurrentPosition(latlng);
        setMapCenter(latlng);
      },
      (error) => {
        console.warn("Unable to fetch current location:", error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, []);

  // Scheduled date/time
  const [scheduledFrom, setScheduledFrom] = useState("");
  const [scheduledTo, setScheduledTo] = useState("");

  // Custom Vehicle Specs
  const [customFeature, setCustomFeature] = useState("");
  const [customTransmission, setCustomTransmission] = useState("");

  const [formData, setFormData] = useState({
    userName: "",
    phoneNumber: "",
    userEmail: "",
    gender: "",
    regNo: "",
    vehicleType: "",
    make: "",
    vehicleName: "",
    features: [],
    transmission: [],
    maxSpeed: 50,
    model: "",
    year: "",
    fuelType: "hybrid",
    color: "",
    status: "PENDING",
    passengerId: "",
    isActingDriverTrip: true,
  });

  const [estimatedFare, setEstimatedFare] = useState(null);
  const [isCalculatingFare, setIsCalculatingFare] = useState(false);
  const [fareError, setFareError] = useState(null);

  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isFetchingVehicle, setIsFetchingVehicle] = useState(false);
  const [passengerExists, setPassengerExists] = useState(null);
  const [hasCheckedPhone, setHasCheckedPhone] = useState(false);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");

  const handleVerifyPhone = async () => {
    if (!formData.phoneNumber) {
      toast.error("Please enter a phone number to verify.");
      return;
    }

    setIsCheckingPhone(true);
    try {
      console.log("Verifying phone:", formData.phoneNumber);
      const response = await api.get(
        `/trips/passenger-vehicle-info/${formData.phoneNumber}`,
      );
      console.log("Verification response:", response.data);

      if (response.data.exists) {
        setPassengerExists(true);
        const vehicles = response.data.vehicles || [];
        setAvailableVehicles(vehicles);

        let autoFormValues = {
          userName: response.data.passenger.name || "",
          passengerId: response.data.passenger._id || "",
          userEmail: response.data.passenger.email || "",
          gender: response.data.passenger.gender || "",
        };

        // If exactly one vehicle, auto-select and auto-fill it
        if (vehicles.length === 1) {
          const v = vehicles[0];
          setSelectedVehicleId(v._id);
          autoFormValues = {
            ...autoFormValues,
            regNo: v.regNo || "",
            vehicleType: v.type || "",
            make: v.make || "",
            vehicleName: v.vehicleName || v.model || v.make || "",
            model: v.model || "",
            year: v.year ? v.year.split("-")[0] : "",
            fuelType: v.fuelType || "",
            color: v.color || "",
            features: v.features || [],
            transmission: v.transmission || [],
            maxSpeed: v.maxSpeed || 50,
          };
        } else if (vehicles.length > 1) {
          setSelectedVehicleId("");
        }

        setFormData((prev) => ({
          ...prev,
          ...autoFormValues,
        }));

        toast.success(
          `Passenger found. ${vehicles.length === 1 ? "Vehicle details auto-filled." : "Please select a vehicle."}`,
        );
      } else {
        setPassengerExists(false);
        setAvailableVehicles([]);
        setFormData((prev) => ({
          ...prev,
          userName: "",
          passengerId: "",
          userEmail: "",
          gender: "",
          regNo: "",
          vehicleType: "",
          make: "",
          vehicleName: "",
          model: "",
          year: "",
          fuelType: "",
          color: "",
          features: [],
          transmission: [],
          maxSpeed: 50,
        }));
        toast("New passenger. Please fill all details manually.", {
          icon: "ℹ️",
        });
      }
      setHasCheckedPhone(true);
    } catch (err) {
      console.error("Full verification error:", err);
      const errorMessage =
        err.response?.data?.message || "Error verifying phone number.";
      toast.error(errorMessage);
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const handleFetchVehicleDetails = async () => {
    if (!formData.regNo) {
      toast.error("Please enter a registration number first.");
      return;
    }
    setIsFetchingVehicle(true);
    try {
      const res = await api.get(`/trips/parivahan-info/${formData.regNo}`);
      if (res.data.success && res.data.data) {
        const vehicleData = res.data.data;
        setFormData((prev) => ({
          ...prev,
          make: vehicleData.make || prev.make,
          model: vehicleData.model || prev.model,
          vehicleType: vehicleData.type || prev.vehicleType,
          fuelType: vehicleData.fuelType || prev.fuelType,
          color: vehicleData.color || prev.color,
          year: vehicleData.year ? vehicleData.year.split("-")[0] : prev.year,
          vehicleName: prev.vehicleName || `${vehicleData.make || ''} ${vehicleData.model || ''}`.trim()
        }));
        toast.success("Vehicle details fetched successfully!");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch vehicle details.");
    } finally {
      setIsFetchingVehicle(false);
    }
  };

  const handleSelectVehicle = (e) => {
    const vehicleId = e.target.value;
    setSelectedVehicleId(vehicleId);
    if (!vehicleId) {
      setFormData((prev) => ({
        ...prev,
        userEmail: "",
        gender: "",
        regNo: "",
        vehicleType: "",
        make: "",
        vehicleName: "",
        model: "",
        year: "",
        fuelType: "",
        color: "",
        features: [],
        transmission: [],
        maxSpeed: 50,
      }));
      return;
    }
    const vehicle = availableVehicles.find((v) => v._id === vehicleId);
    if (vehicle) {
      setFormData((prev) => ({
        ...prev,
        regNo: vehicle.regNo || "",
        vehicleType: vehicle.type || "",
        make: vehicle.make || "",
        vehicleName: vehicle.vehicleName || vehicle.model || vehicle.make || "",
        model: vehicle.model || "",
        year: vehicle.year ? vehicle.year.split("-")[0] : "",
        fuelType: vehicle.fuelType || "",
        color: vehicle.color || "",
        features: vehicle.features || [],
        transmission: vehicle.transmission || [],
        maxSpeed: vehicle.maxSpeed || 50,
      }));
    }
  };

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const latlng = [latitude, longitude];
        setFromPosition(latlng);
        setMapCenter(latlng);
        const name = await reverseGeocode(latitude, longitude);
        setFromLabel(name);
        setGettingLocation(false);
        toast.success("Current location set as starting point!");
      },
      (err) => {
        setGettingLocation(false);
        toast.error(
          "Unable to get your location. Please allow location access.",
        );
      },
      { timeout: 10000 },
    );
  }, []);

  // Autocomplete: fetch suggestions with debounce
  const handleFromSearchChange = (e) => {
    const val = e.target.value;
    setFromSearchText(val);
    setShowFromSuggestions(true);
    clearTimeout(fromDebounceRef.current);
    fromDebounceRef.current = setTimeout(async () => {
      const suggestions = await fetchSuggestions(val);
      setFromSuggestions(suggestions);
    }, 400);
  };

  const handleToSearchChange = (e) => {
    const val = e.target.value;
    setToSearchText(val);
    setShowToSuggestions(true);
    clearTimeout(toDebounceRef.current);
    toDebounceRef.current = setTimeout(async () => {
      const suggestions = await fetchSuggestions(val);
      setToSuggestions(suggestions);
    }, 400);
  };

  const selectFromSuggestion = (item) => {
    setFromSearchText(item.name);
    setFromPosition([item.lat, item.lng]);
    setMapCenter([item.lat, item.lng]);
    setFromLabel(item.name);
    setShowFromSuggestions(false);
    setActivePinField(null);
  };

  const selectToSuggestion = (item) => {
    setToSearchText(item.name);
    setToPosition([item.lat, item.lng]);
    setMapCenter([item.lat, item.lng]);
    setToLabel(item.name);
    setShowToSuggestions(false);
    setActivePinField(null);
  };

  const handleStopSearchChange = (e) => {
    const val = e.target.value;
    setStopSearchText(val);
    setShowStopSuggestions(true);
    clearTimeout(stopDebounceRef.current);
    stopDebounceRef.current = setTimeout(async () => {
      const suggestions = await fetchSuggestions(val);
      setStopSuggestions(suggestions);
    }, 400);
  };

  const selectStopSuggestion = (item) => {
    setStops((prev) => [
      ...prev,
      { lat: item.lat, lng: item.lng, name: item.name },
    ]);
    setMapCenter([item.lat, item.lng]);
    setStopSearchText("");
    setShowStopSuggestions(false);
    setActivePinField(null);
    toast.success("Stop added!");
  };

  // Search "From" address (button click)
  const handleSearchFrom = useCallback(async () => {
    if (!fromSearchText.trim()) return;
    setSearchingFrom(true);
    try {
      const result = await geocodeAddress(fromSearchText);
      if (result) {
        setFromPosition([result.lat, result.lng]);
        setMapCenter([result.lat, result.lng]);
        setFromLabel(result.name);
        setFromSearchText(result.name);
        setShowFromSuggestions(false);
        setActivePinField(null);
        toast.success("From location found!");
      } else {
        toast.error("Location not found. Try a different search.");
      }
    } catch {
      toast.error("Error searching location.");
    } finally {
      setSearchingFrom(false);
    }
  }, [fromSearchText]);

  // Search "To" address (button click)
  const handleSearchTo = useCallback(async () => {
    if (!toSearchText.trim()) return;
    setSearchingTo(true);
    try {
      const result = await geocodeAddress(toSearchText);
      if (result) {
        setToPosition([result.lat, result.lng]);
        setMapCenter([result.lat, result.lng]);
        setToLabel(result.name);
        setToSearchText(result.name);
        setShowToSuggestions(false);
        setActivePinField(null);
        toast.success("Destination found!");
      } else {
        toast.error("Destination not found. Try a different search.");
      }
    } catch {
      toast.error("Error searching destination.");
    } finally {
      setSearchingTo(false);
    }
  }, [toSearchText]);

  const handleSearchStop = useCallback(async () => {
    if (!stopSearchText.trim()) return;
    setSearchingStop(true);
    try {
      const result = await geocodeAddress(stopSearchText);
      if (result) {
        setStops((prev) => [
          ...prev,
          { lat: result.lat, lng: result.lng, name: result.name },
        ]);
        setMapCenter([result.lat, result.lng]);
        setStopSearchText("");
        setShowStopSuggestions(false);
        setActivePinField(null);
        toast.success("Stop added!");
      } else {
        toast.error("Location not found.");
      }
    } catch {
      toast.error("Error searching location.");
    } finally {
      setSearchingStop(false);
    }
  }, [stopSearchText]);

  const removeStop = (index) => {
    setStops((prev) => prev.filter((_, i) => i !== index));
  };

  // Map click handler — sets From or To based on activePinField
  const handleMapClick = useCallback(
    async (latlng, field) => {
      let targetField = field;

      if (!targetField) {
        if (!fromPosition) targetField = "from";
        else if (!toPosition) targetField = "to";
        else if (showStopsUI) targetField = "stop";
      }

      if (!targetField) return;

      setActivePinField(null);

      if (targetField === "from") {
        setFromPosition([latlng.lat, latlng.lng]);
        setMapCenter([latlng.lat, latlng.lng]);
        setFromLabel("Locating address...");
        setFromSearchText("Locating address...");
      } else if (targetField === "to") {
        setToPosition([latlng.lat, latlng.lng]);
        setMapCenter([latlng.lat, latlng.lng]);
        setToLabel("Locating address...");
        setToSearchText("Locating address...");
      } else if (targetField === "stop") {
        // Add a placeholder stop immediately
        const placeholderStop = {
          lat: latlng.lat,
          lng: latlng.lng,
          name: "Locating address...",
          id: Date.now(),
        };
        setStops((prev) => [...prev, placeholderStop]);
      }

      // 2. Background geocoding
      try {
        const name = await reverseGeocode(latlng.lat, latlng.lng);

        if (targetField === "from") {
          setFromLabel(name);
          setFromSearchText(name);
          toast.success("Starting point pinned!");
        } else if (targetField === "to") {
          setToLabel(name);
          setToSearchText(name);
          toast.success("Destination pinned!");
        } else if (targetField === "stop") {
          // Update the last added stop with the actual name
          setStops((prev) => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i].name === "Locating address...") {
                updated[i] = { ...updated[i], name };
                break;
              }
            }
            return updated;
          });
          toast.success("Stop added!");
        }
      } catch (error) {
        console.error("Geocoding failed:", error);
        // Clean up "Locating..." labels if they exist
        if (targetField === "from") setFromLabel(latlng.lat.toFixed(5));
        else if (targetField === "to") setToLabel(latlng.lat.toFixed(5));
      }
    },
    [fromPosition, toPosition, showStopsUI],
  );

  const clearFrom = () => {
    setFromPosition(null);
    setFromLabel("");
    setFromSearchText("");
    toast.success("Starting point cleared");
  };

  const clearTo = () => {
    setToPosition(null);
    setToLabel("");
    setToSearchText("");
    toast.success("Destination cleared");
  };

  // Fetch route from OSRM
  useEffect(() => {
    const fetchRoute = async () => {
      if (!fromPosition || !toPosition) {
        setRoutePoints([]);
        return;
      }

      try {
        const waypoints = [
          [fromPosition[1], fromPosition[0]],
          ...stops.map((s) => [s.lng, s.lat]),
          [toPosition[1], toPosition[0]],
        ];

        const coordinatesString = waypoints
          .map((point) => point.join(","))
          .join(";");
        const response = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${coordinatesString}?overview=full&geometries=polyline`,
        );
        const data = await response.json();

        if (data.code === "Ok" && data.routes && data.routes.length > 0) {
          const decodedPoints = polyline.decode(data.routes[0].geometry);
          setRoutePoints(decodedPoints);
          setEstimatedDistance(data.routes[0].distance);
          setEstimatedDuration(data.routes[0].duration);
        } else {
          setRoutePoints([]);
          setEstimatedDistance(0);
          setEstimatedDuration(0);
        }
      } catch (error) {
        console.error("Routing failed:", error);
        setRoutePoints([]);
      }
    };

    const timeoutId = setTimeout(fetchRoute, 500); // Debounce route fetching
    return () => clearTimeout(timeoutId);
  }, [fromPosition, toPosition, stops]);

  // Estimate fare whenever route or vehicle type changes
  useEffect(() => {
    const estimateFare = async () => {
      if (!fromPosition || !toPosition || !formData.vehicleType) {
        setEstimatedFare(null);
        setFareError(null);
        return;
      }

      setIsCalculatingFare(true);
      setFareError(null);

      try {
        const payload = {
          pickupLocation: [fromPosition[1], fromPosition[0]], // [lng, lat]
          vehicleType: formData.vehicleType,
          distance: estimatedDistance,
          duration: estimatedDuration
        };

        const response = await api.post("/fare/estimate", payload);
        if (response.data.success) {
          setEstimatedFare(response.data.data);
        } else {
          setFareError(response.data.message || "Could not calculate fare.");
        }
      } catch (err) {
        console.error("Fare estimation error:", err);
        setFareError(err.response?.data?.message || "Fare calculation failed.");
        setEstimatedFare(null);
      } finally {
        setIsCalculatingFare(false);
      }
    };

    const timeoutId = setTimeout(estimateFare, 600); // Wait slightly longer than fetchRoute
    return () => clearTimeout(timeoutId);
  }, [fromPosition, toPosition, stops, formData.vehicleType, estimatedDistance, estimatedDuration]);

  const handleMapDoubleClick = useCallback(
    async (latlng) => {
      // Now handled by single click if stops are enabled
    },
    [],
  );

  // Draggable markers
  const handleFromMarkerDrag = useCallback(async (e) => {
    const { lat, lng } = e.target.getLatLng();
    setFromPosition([lat, lng]);
    const name = await reverseGeocode(lat, lng);
    setFromLabel(name);
    setFromSearchText(name);
  }, []);

  const handleToMarkerDrag = useCallback(async (e) => {
    const { lat, lng } = e.target.getLatLng();
    setToPosition([lat, lng]);
    const name = await reverseGeocode(lat, lng);
    setToLabel(name);
    setToSearchText(name);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phoneNumber") {
      // Allow only digits, max 10
      const digits = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, phoneNumber: digits }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFeatureToggle = (value) => {
    setFormData((prev) => {
      const features = prev.features.includes(value)
        ? prev.features.filter((f) => f !== value)
        : [...prev.features, value];
      return { ...prev, features };
    });
  };

  const handleTransmissionToggle = (value) => {
    setFormData((prev) => {
      let newTransmission = prev.transmission.includes(value)
        ? prev.transmission.filter((t) => t !== value)
        : [...prev.transmission, value];

      // If "automatic" is unchecked, also uncheck all automatic sub-options
      if (value === "automatic" && prev.transmission.includes("automatic")) {
        const subOptionValues = AUTOMATIC_SUB_OPTIONS.map((o) => o.value);
        newTransmission = newTransmission.filter(
          (t) => !subOptionValues.includes(t),
        );
      }

      return { ...prev, transmission: newTransmission };
    });
  };

  const handleAddCustomFeature = () => {
    if (
      customFeature.trim() &&
      !formData.features.includes(customFeature.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, customFeature.trim()],
      }));
      setCustomFeature("");
    }
  };

  const handleAddCustomTransmission = () => {
    if (
      customTransmission.trim() &&
      !formData.transmission.includes(customTransmission.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        transmission: [...prev.transmission, customTransmission.trim()],
      }));
      setCustomTransmission("");
    }
  };

  // Helper: scroll to an element by id and focus it
  const scrollToField = (id, message) => {
    toast.error(message);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => el.focus(), 400);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.phoneNumber || formData.phoneNumber.length !== 10) {
      scrollToField("field-phoneNumber", "Please enter a valid 10-digit phone number.");
      return;
    }
    if (!formData.userName.trim()) {
      scrollToField("field-userName", "Please enter the passenger name.");
      return;
    }
    if (!fromPosition) {
      scrollToField("field-fromSearch", "Please set a starting location (From).");
      return;
    }
    if (!toPosition) {
      scrollToField("field-toSearch", "Please set a destination (To).");
      return;
    }
    if (!formData.vehicleName.trim()) {
      scrollToField("field-vehicleName", "Please enter the vehicle name.");
      return;
    }

    const payload = {
      from: {
        coordinates: [fromPosition[1], fromPosition[0]], // [lng, lat]
        address: fromLabel,
      },
      to: {
        coordinates: [toPosition[1], toPosition[0]],
        address: toLabel,
      },
      stops: stops.map((stop) => ({
        coordinates: [stop.lng, stop.lat],
        address: stop.name,
      })),
      userName: formData.userName,
      phoneNumber: formData.phoneNumber,
      userEmail: formData.userEmail ? formData.userEmail.trim() : null,
      gender: formData.gender,
      passengerId: formData.passengerId || null,
      regNo: formData.regNo,
      vehicleType: formData.vehicleType,
      make: formData.make,
      vehicleName: formData.vehicleName,
      vehicleSpecification: {
        features: formData.features,
        transmission: formData.transmission,
        maxSpeed: Number(formData.maxSpeed),
        model: formData.model,
        year: formData.year,
        fuelType: formData.fuelType,
        color: formData.color,
      },
      status: formData.status,
      isActingDriverTrip: formData.isActingDriverTrip,
      scheduledFrom: scheduledFrom || null,
      scheduledTo: scheduledTo || null,
    };

    setIsSubmitting(true);
    try {
      if (formData.isActingDriverTrip) {
        // Acting bookings go through the rides backend so they are priced by the
        // real acting fare service and handed to the matching engine. The legacy
        // /create-trip below writes scheduledFrom/scheduledTo and no
        // actingDetails, which the engine and the trip details panel ignore.
        const startMs = scheduledFrom ? new Date(scheduledFrom).getTime() : Date.now();
        const endMs = scheduledTo ? new Date(scheduledTo).getTime() : null;
        const hours = endMs && endMs > startMs
          ? Math.max(1, Math.round(((endMs - startMs) / 3600000) * 10) / 10)
          : 4;

        const res = await api.post("/trips/acting/create", {
          latitude: fromPosition[0],
          longitude: fromPosition[1],
          pickupAddress: fromLabel,
          dropAddress: toLabel,
          scheduleDateTime: startMs,
          actingDriverHours: hours,
          vehicleType: formData.vehicleType,
          passenger: {
            name: formData.userName,
            phone: formData.phoneNumber,
            email: formData.userEmail ? formData.userEmail.trim() : undefined,
            gender: formData.gender,
          },
          customerVehicle: {
            regNo: formData.regNo,
            make: formData.make,
            model: formData.model,
            type: formData.vehicleType,
            transmission: formData.transmission,
            fuelType: formData.fuelType,
          },
        });

        const d = res?.data?.data;
        toast.success(
          d?.rideId
            ? `Acting trip ${d.rideId} created — drivers are being notified.`
            : "Acting trip created — drivers are being notified."
        );
        navigate("/trips");
        return;
      }

      await api.post("/trips/create-trip", payload);
      toast.success("Trip created successfully!");
      navigate("/trips");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create trip.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center space-x-4">
            <Link
              to="/trips"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="page-title">Create Trip</h1>
              <p className="page-subtitle text-gray-500 text-sm mt-1">
                Plan a new trip with route and vehicle details
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 px-4 sm:px-6 pb-8">
        {/* TRIP TYPE — the page used to hardcode isActingDriverTrip: true with no
            control, so every trip created here was an acting booking and nothing
            said so. The two kinds take different backends and price differently,
            so the choice belongs up front. */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Car className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Trip Type</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              What kind of booking is this?
            </p>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  acting: true,
                  label: 'Acting Driver',
                  icon: '🚘',
                  desc: "Driver drives the customer's own car, booked by the hour. Priced from the region's acting fare config and sent straight to the matching engine.",
                },
                {
                  acting: false,
                  label: 'Normal Ride',
                  icon: '🚕',
                  desc: 'A standard point-to-point ride in a fleet vehicle.',
                },
              ].map((opt) => {
                const selected = Boolean(formData.isActingDriverTrip) === opt.acting;
                return (
                  <div
                    key={opt.label}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, isActingDriverTrip: opt.acting }))
                    }
                    className={`flex flex-col gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all select-none
                      ${selected
                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-400'
                        : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  >
                    <span className="text-3xl">{opt.icon}</span>
                    <p className={`font-semibold text-sm ${selected ? 'text-primary-700' : 'text-gray-800'}`}>
                      {opt.label}
                    </p>
                    <p className="text-xs text-gray-500">{opt.desc}</p>
                    {selected && <span className="text-xs font-medium text-primary-700">✓ Selected</span>}
                  </div>
                );
              })}
            </div>

            {formData.isActingDriverTrip && (
              <p className="mt-4 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                The fare is calculated on the server from the pickup region and the
                hours between <strong>Scheduled From</strong> and <strong>Scheduled To</strong> —
                there is no fare field to fill in. Drivers are notified as soon as you save.
              </p>
            )}
          </div>
        </div>

        {/* MAP SECTION */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <MapPin className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Route Selection
              </h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Set your starting point and destination on the map
            </p>
          </div>
          <div className="card-body space-y-4">
            {/* DATE row (New) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label flex items-center space-x-1">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>Scheduled From</span>
                </label>
                <input
                  type="datetime-local"
                  className="input"
                  value={scheduledFrom}
                  min={getNow()}
                  onChange={(e) => {
                    const picked = e.target.value;
                    const now = getNow();
                    // Snap to current time if user picks a past time
                    const finalFrom = picked < now ? now : picked;
                    setScheduledFrom(finalFrom);
                    // Reset scheduledTo if it's now before the new scheduledFrom
                    if (scheduledTo && finalFrom && scheduledTo < finalFrom) {
                      setScheduledTo("");
                    }
                  }}
                />
              </div>
              <div>
                <label className="form-label flex items-center space-x-1">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>Scheduled To</span>
                </label>
                <input
                  type="datetime-local"
                  className="input"
                  value={scheduledTo}
                  min={scheduledFrom || getNow()}
                  onChange={(e) => {
                    const picked = e.target.value;
                    const minAllowed = scheduledFrom || getNow();
                    // Snap to minimum if user picks a past time or before scheduledFrom
                    const finalTo = picked < minAllowed ? minAllowed : picked;
                    setScheduledTo(finalTo);
                  }}
                />
              </div>
            </div>

            {/* FROM row */}
            <div className="space-y-2">
              <label className="form-label flex items-center space-x-1">
                <span className="h-3 w-3 rounded-full bg-green-500 inline-block"></span>
                <span>From (Starting Point)</span>
                {activePinField === "from" && (
                  <span className="text-xs text-blue-600 ml-2 animate-pulse flex items-center">
                    <MapPin className="h-3 w-3 mr-1" /> Click map to pin start
                  </span>
                )}
              </label>
              <div className="flex gap-2 flex-wrap">
                <div className="relative flex-1 min-w-0">
                  <input
                    id="field-fromSearch"
                    type="text"
                    className="input w-full pr-10"
                    placeholder="Type a location to search..."
                    value={fromSearchText}
                    onChange={handleFromSearchChange}
                    onFocus={() => setActivePinField("from")}
                    onBlur={() =>
                      setTimeout(() => {
                        setShowFromSuggestions(false);
                      }, 200)
                    }
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), handleSearchFrom())
                    }
                  />
                  {fromSearchText && (
                    <button
                      type="button"
                      onClick={clearFrom}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Clear starting point"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {showFromSuggestions && fromSuggestions.length > 0 && (
                    <ul className="absolute z-10 w-full bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-auto">
                      {fromSuggestions.map((item, i) => (
                        <li
                          key={i}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                          onClick={() => selectFromSuggestion(item)}
                        >
                          {item.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSearchFrom}
                  disabled={searchingFrom}
                  className="btn btn-outline flex items-center gap-1"
                >
                  {searchingFrom ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  Search
                </button>
                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  disabled={gettingLocation}
                  className="btn btn-primary flex items-center gap-1"
                >
                  {gettingLocation ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Navigation className="h-4 w-4" />
                  )}
                  Use My Location
                </button>
              </div>
              {fromLabel && (
                <p className="text-xs text-green-700 bg-green-50 rounded px-2 py-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {fromLabel}
                </p>
              )}
            </div>

            {/* Add Stop Toggle Button */}
            {!showStopsUI && (
              <div className="flex justify-center -my-2 relative z-10">
                <button
                  type="button"
                  onClick={() => {
                    if (!fromPosition) {
                      toast.error(
                        "Please set a starting point first before adding stops.",
                      );
                      return;
                    }
                    setShowStopsUI(true);
                    setActivePinField("stop");
                  }}
                  className="bg-white border border-gray-200 shadow-sm text-gray-600 hover:text-primary-600 hover:border-primary-300 text-xs py-1 px-3 rounded-full flex items-center gap-1 transition-colors"
                >
                  <MapPin className="h-3 w-3" />
                  Add Stop
                </button>
              </div>
            )}

            {/* STOPS row */}
            {showStopsUI && (
              <div className="space-y-2">
                <label className="form-label flex items-center space-x-1">
                  <span className="h-3 w-3 rounded-full bg-blue-500 inline-block"></span>
                  <span>Stops (Click map or search to add)</span>
                  {activePinField === "stop" && (
                    <span className="text-xs text-blue-600 ml-2 animate-pulse flex items-center">
                      <MapPin className="h-3 w-3 mr-1" /> Click map to add stop
                    </span>
                  )}
                </label>

                <div className="flex gap-2 flex-wrap">
                  <div className="relative flex-1 min-w-0">
                    <input
                      type="text"
                      className="input w-full pr-10"
                      placeholder="Search and add a stop..."
                      value={stopSearchText}
                      onChange={handleStopSearchChange}
                      onFocus={() => setActivePinField("stop")}
                      onBlur={() =>
                        setTimeout(() => {
                          setShowStopSuggestions(false);
                        }, 200)
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleSearchStop())
                      }
                    />
                    {stopSearchText && (
                      <button
                        type="button"
                        onClick={() => setStopSearchText("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                    {showStopSuggestions && stopSuggestions.length > 0 && (
                      <ul className="absolute z-10 w-full bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-auto">
                        {stopSuggestions.map((item, i) => (
                          <li
                            key={i}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                            onClick={() => selectStopSuggestion(item)}
                          >
                            {item.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleSearchStop}
                    disabled={searchingStop}
                    className="btn btn-outline flex items-center gap-1"
                  >
                    {searchingStop ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    Search
                  </button>
                </div>

                {stops.length > 0 && (
                  <div className="flex flex-col gap-2 mt-2">
                    {stops.map((stop, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center text-xs text-blue-800 bg-blue-50 rounded px-2 py-1"
                      >
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          <span className="font-semibold">
                            Stop {index + 1}:
                          </span>{" "}
                          {stop.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeStop(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TO row */}
            <div className="space-y-2">
              <label className="form-label flex items-center space-x-1">
                <span className="h-3 w-3 rounded-full bg-red-500 inline-block"></span>
                <span>To (Destination)</span>
                {activePinField === "to" && (
                  <span className="text-xs text-blue-600 ml-2 animate-pulse flex items-center">
                    <MapPin className="h-3 w-3 mr-1" /> Click map to pin
                    destination
                  </span>
                )}
              </label>
              <div className="flex gap-2 flex-wrap">
                <div className="relative flex-1 min-w-0">
                  <input
                    id="field-toSearch"
                    type="text"
                    className="input w-full pr-10"
                    placeholder="Type destination or drop pin on map..."
                    value={toSearchText}
                    onChange={handleToSearchChange}
                    onFocus={() => setActivePinField("to")}
                    onBlur={() =>
                      setTimeout(() => {
                        setShowToSuggestions(false);
                      }, 200)
                    }
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), handleSearchTo())
                    }
                  />
                  {toSearchText && (
                    <button
                      type="button"
                      onClick={clearTo}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Clear destination"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {showToSuggestions && toSuggestions.length > 0 && (
                    <ul className="absolute z-10 w-full bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-auto">
                      {toSuggestions.map((item, i) => (
                        <li
                          key={i}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                          onClick={() => selectToSuggestion(item)}
                        >
                          {item.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSearchTo}
                  disabled={searchingTo}
                  className="btn btn-outline flex items-center gap-1"
                >
                  {searchingTo ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  Search
                </button>
              </div>
              {toLabel && (
                <p className="text-xs text-red-700 bg-red-50 rounded px-2 py-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {toLabel}
                </p>
              )}
            </div>

            {/* Map */}
            <div
              className="rounded-lg overflow-hidden border border-gray-200"
              style={{
                height: "400px",
                cursor: activePinField ? "crosshair" : "grab",
              }}
            >
              <MapContainer
                center={mapCenter}
                zoom={6}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
                doubleClickZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <FlyToLocation position={mapCenter} />

                <MapPinHandler
                  onMapClick={handleMapClick}
                  activePinField={activePinField}
                />

                {routePoints.length > 0 && (
                  <Polyline
                    positions={routePoints}
                    color="#3B82F6"
                    weight={4}
                    opacity={0.8}
                  />
                )}

                {stops.map((stop, i) => (
                  <Marker
                    key={i}
                    position={[stop.lat, stop.lng]}
                    draggable={true}
                    eventHandlers={{
                      dragend: async (e) => {
                        const { lat, lng } = e.target.getLatLng();
                        const name = await reverseGeocode(lat, lng);
                        setStops((prev) =>
                          prev.map((s, idx) =>
                            idx === i ? { ...s, lat, lng, name } : s,
                          ),
                        );
                      },
                      dblclick: () => {
                        removeStop(i);
                        toast.success("Stop removed!");
                      },
                    }}
                  >
                    <Popup>
                      <strong>🛑 Stop {i + 1}</strong>
                      <br />
                      <span className="text-xs">{stop.name}</span>
                    </Popup>
                  </Marker>
                ))}

                {fromPosition && (
                  <Marker
                    position={fromPosition}
                    icon={fromIcon}
                    draggable={true}
                    eventHandlers={{ dragend: handleFromMarkerDrag }}
                  >
                    <Popup>
                      <strong>📍 Starting Point</strong>
                      <br />
                      <span className="text-xs">
                        {fromLabel || "Your location"}
                      </span>
                      <br />
                      <span className="text-xs text-gray-400">
                        Drag to adjust
                      </span>
                    </Popup>
                  </Marker>
                )}

                {toPosition && (
                  <Marker
                    position={toPosition}
                    icon={toIcon}
                    draggable={true}
                    eventHandlers={{ dragend: handleToMarkerDrag }}
                  >
                    <Popup>
                      <strong>🏁 Destination</strong>
                      <br />
                      <span className="text-xs">
                        {toLabel || "Destination"}
                      </span>
                      <br />
                      <span className="text-xs text-gray-400">
                        Drag to adjust
                      </span>
                    </Popup>
                  </Marker>
                )}

                {currentPosition && (
                  <Marker position={currentPosition} icon={currentLocationIcon}>
                    <Popup>
                      <strong>📍 Your Current Location</strong>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Passenger Information
              </h2>
            </div>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  <Phone className="h-4 w-4 inline mr-1" />
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    id="field-phoneNumber"
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="input flex-1"
                    placeholder="e.g. 9876543210"
                    required
                  />
                  {!hasCheckedPhone && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleVerifyPhone}
                      disabled={
                        isCheckingPhone || formData.phoneNumber.length < 10
                      }
                    >
                      {isCheckingPhone ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        "Verify"
                      )}
                    </button>
                  )}
                  {hasCheckedPhone && (
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setHasCheckedPhone(false);
                        setPassengerExists(null);
                        setAvailableVehicles([]);
                        setFormData((prev) => ({
                          ...prev,
                          userName: "",
                          passengerId: "",
                          regNo: "",
                          vehicleName: "",
                          make: "",
                          vehicleType: "",
                          model: "",
                          year: "",
                          fuelType: "",
                          color: "",
                          features: [],
                          transmission: [],
                          maxSpeed: 50,
                        }));
                        setSelectedVehicleId("");
                      }}
                    >
                      Change
                    </button>
                  )}
                </div>
              </div>

              {hasCheckedPhone && (
                <div>
                  <label className="form-label">
                    <User className="h-4 w-4 inline mr-1" />
                    User Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="field-userName"
                    type="text"
                    name="userName"
                    value={formData.userName}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter user's full name"
                    required
                  />
                </div>
              )}

              {hasCheckedPhone && (
                <div>
                  <label className="form-label">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="input"
                  >
                    <option value="">Select Gender</option>
                    {GENDER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {hasCheckedPhone && (
                <div>
                  <label className="form-label">
                    Email<span className="text-gray-500"> (optional)</span>
                  </label>
                  <input
                    type="text"
                    name="userEmail"
                    value={formData.userEmail}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="example@gmail.com"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {hasCheckedPhone && (
          <>
            {/* VEHICLE INFO */}
            <div className="card">
              <div className="card-header">
                <div className="flex justify-between items-center w-full">
                  <div className="flex items-center space-x-2">
                    <Car className="h-5 w-5 text-primary-600" />
                    <h2 className="text-lg font-semibold text-gray-900">
                      Vehicle Information
                    </h2>
                  </div>
                  {availableVehicles.length > 0 && (
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600 font-medium whitespace-nowrap">
                        Select Vehicle:
                      </label>
                      <select
                        className="input py-1 px-3 h-9"
                        value={selectedVehicleId}
                        onChange={handleSelectVehicle}
                      >
                        <option value="">-- Add New Vehicle --</option>
                        {availableVehicles.map((v) => (
                          <option key={v._id} value={v._id}>
                            {v.regNo} - {v.vehicleName || v.model || v.make}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="card-body space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Registration Number <span className="text-red-500">*</span></label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="regNo"
                        value={formData.regNo}
                        onChange={handleInputChange}
                        className="input flex-1"
                        placeholder="e.g. TN66BB5555"
                        required
                      />
                      {!selectedVehicleId && (
                        <button
                          type="button"
                          onClick={handleFetchVehicleDetails}
                          disabled={isFetchingVehicle || !formData.regNo}
                          className="btn btn-primary whitespace-nowrap"
                        >
                          {isFetchingVehicle ? <Loader className="h-4 w-4 animate-spin" /> : "Fetch Details"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="form-label">
                      Vehicle Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="field-vehicleName"
                      type="text"
                      name="vehicleName"
                      value={formData.vehicleName}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="e.g. Skoda Kushaq"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Make (Brand)</label>
                    <input
                      type="text"
                      name="make"
                      value={formData.make}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="e.g. Skoda, Toyota, Hyundai"
                    />
                  </div>
                  <div>
                    <label className="form-label">Vehicle Type</label>
                    <select
                      name="vehicleType"
                      value={formData.vehicleType}
                      onChange={handleInputChange}
                      className="input"
                    >
                      <option value="">Select vehicle type</option>
                      {VEHICLE_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Model</label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="e.g. Kushaq, Creta"
                    />
                  </div>
                  <div>
                    <label className="form-label">Year</label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="e.g. 2017"
                      min="1990"
                      max={new Date().getFullYear() + 1}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Fuel Type</label>
                    <select
                      name="fuelType"
                      value={formData.fuelType}
                      onChange={handleInputChange}
                      className="input"
                    >
                      <option value="">Select fuel type</option>
                      {FUEL_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Color</label>
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      className="input"
                      placeholder="e.g. White, Black, Silver"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* VEHICLE SPEC */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center space-x-2">
                  <Settings className="h-5 w-5 text-primary-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Vehicle Specifications
                  </h2>
                </div>
              </div>
              <div className="card-body space-y-6">
                <div>
                  <label className="form-label mb-2 block">
                    Transmission Types
                  </label>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {TRANSMISSION_OPTIONS.map((opt) => {
                        const checked = formData.transmission.includes(opt.value);
                        return (
                          <label
                            key={opt.value}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors text-sm ${
                              checked
                                ? "border-primary-500 bg-primary-50 text-primary-700"
                                : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                              checked={checked}
                              onChange={() => handleTransmissionToggle(opt.value)}
                            />
                            {opt.label}
                          </label>
                        );
                      })}
                    </div>

                    {formData.transmission.includes("automatic") && (
                      <div className="pl-4 border-l-2 border-primary-100 space-y-2">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Automatic Sub-types
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {AUTOMATIC_SUB_OPTIONS.map((opt) => {
                            const checked = formData.transmission.includes(
                              opt.value,
                            );
                            return (
                              <label
                                key={opt.value}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors text-sm ${
                                  checked
                                    ? "border-blue-500 bg-blue-50 text-blue-700"
                                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                  checked={checked}
                                  onChange={() =>
                                    handleTransmissionToggle(opt.value)
                                  }
                                />
                                {opt.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2 max-w-sm">
                    <input
                      type="text"
                      className="input flex-1 min-w-0"
                      placeholder="Add custom..."
                      value={customTransmission}
                      onChange={(e) => setCustomTransmission(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCustomTransmission();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={handleAddCustomTransmission}
                    >
                      Add
                    </button>
                  </div>
                  {formData.transmission.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.transmission.map((t) => {
                        return (
                          <span
                            key={t}
                            className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-xs text-gray-700 border border-gray-200"
                          >
                            {(() => {
                              const opt = TRANSMISSION_OPTIONS.find(
                                (o) => o.value === t,
                              );
                              const subOpt = AUTOMATIC_SUB_OPTIONS.find(
                                (o) => o.value === t,
                              );
                              return (opt || subOpt)?.label || t;
                            })()}
                            <button
                              type="button"
                              onClick={() => handleTransmissionToggle(t)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    Speed Limit (km/h)
                    <span className="ml-2 text-primary-600 font-semibold">
                      {formData.maxSpeed}
                    </span>
                    <span className="ml-2 text-xs text-gray-400 font-normal">
                      (Min 40 km/h)
                    </span>
                  </label>
                  <input
                    type="range"
                    name="maxSpeed"
                    value={formData.maxSpeed}
                    onChange={handleInputChange}
                    min={40}
                    max={300}
                    step={5}
                    className="w-full accent-primary-600"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>40 km/h</span>
                    <span>300 km/h</span>
                  </div>
                </div>

                <div>
                  <label className="form-label mb-2 block">
                    Safety / Features
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {FEATURES_OPTIONS.map((feat) => {
                      const checked = formData.features.includes(feat.value);
                      return (
                        <label
                          key={feat.value}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors text-sm ${
                            checked
                              ? "border-primary-500 bg-primary-50 text-primary-700"
                              : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                            checked={checked}
                            onChange={() => handleFeatureToggle(feat.value)}
                          />
                          {feat.label}
                        </label>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex items-center gap-2 max-w-sm">
                    <input
                      type="text"
                      className="input flex-1 min-w-0"
                      placeholder="Add custom feature..."
                      value={customFeature}
                      onChange={(e) => setCustomFeature(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCustomFeature();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={handleAddCustomFeature}
                    >
                      Add
                    </button>
                  </div>
                  {formData.features.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.features.map((f) => {
                        const feat = FEATURES_OPTIONS.find(
                          (o) => o.value === f,
                        );
                        return (
                          <span
                            key={f}
                            className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-xs text-gray-700 border border-gray-200"
                          >
                            {feat ? feat.label : f}
                            <button
                              type="button"
                              onClick={() => handleFeatureToggle(f)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {(fromPosition || toPosition) && (
          <div className="card border border-primary-100 bg-primary-50">
            <div className="card-body">
              <h3 className="text-sm font-semibold text-primary-800 mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Route Summary
              </h3>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <span className="h-3 w-3 rounded-full bg-green-500 mt-0.5 flex-shrink-0"></span>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                      From
                    </p>
                    <p className="text-sm text-gray-800">
                      {fromLabel ||
                        (fromPosition
                          ? `${fromPosition[0].toFixed(5)}, ${fromPosition[1].toFixed(5)}`
                          : "Not set")}
                    </p>
                  </div>
                </div>
                {stops &&
                  stops.length > 0 &&
                  stops.map((stop, index) => (
                    <div key={index}>
                      <div className="ml-1.5 border-l-2 border-dashed border-gray-300 h-4"></div>
                      <div className="flex items-start gap-3">
                        <span className="h-3 w-3 rounded-full bg-blue-500 mt-0.5 flex-shrink-0"></span>
                        <div>
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                            Stop {index + 1}
                          </p>
                          <p className="text-sm text-gray-800">
                            {stop.name ||
                              `${stop.lat.toFixed(5)}, ${stop.lng.toFixed(5)}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                <div className="ml-1.5 border-l-2 border-dashed border-gray-300 h-4"></div>
                <div className="flex items-start gap-3">
                  <span className="h-3 w-3 rounded-full bg-red-500 mt-0.5 flex-shrink-0"></span>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                      To
                    </p>
                    <p className="text-sm text-gray-800">
                      {toLabel ||
                        (toPosition
                          ? `${toPosition[0].toFixed(5)}, ${toPosition[1].toFixed(5)}`
                          : "Not set")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Estimated Fare Section */}
              <div className="mt-4 pt-4 border-t border-primary-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-semibold text-primary-800 uppercase tracking-wide">
                      Estimated Fare
                    </h4>
                    {isCalculatingFare ? (
                      <p className="text-sm text-gray-500 animate-pulse">Calculating...</p>
                    ) : fareError ? (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {fareError}
                      </p>
                    ) : estimatedFare ? (
                      <div className="mt-1">
                        <p className="text-2xl font-bold text-primary-900">
                          {estimatedFare.currency === 'INR' ? '₹' : estimatedFare.currency}{estimatedFare.totalFare.toFixed(2)}
                        </p>
                        <p className="text-[10px] text-primary-600 font-medium">
                          Based on {estimatedFare.regionName} rates
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">Set route & vehicle to see fare</p>
                    )}
                  </div>

                  {estimatedFare && !isCalculatingFare && (
                    <div className="text-right">
                      <div className="space-y-0.5">
                        <div className="flex items-center justify-end gap-2 text-[11px] text-gray-500">
                          <span>Distance:</span>
                          <span className="font-semibold text-gray-700">{(estimatedDistance / 1000).toFixed(1)} km</span>
                        </div>
                        <div className="flex items-center justify-end gap-2 text-[11px] text-gray-500">
                          <span>Est. Time:</span>
                          <span className="font-semibold text-gray-700">{Math.round(estimatedDuration / 60)} min</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {estimatedFare && !isCalculatingFare && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-white/50 rounded p-1.5 border border-primary-100">
                      <p className="text-[9px] text-gray-500 uppercase">Base + Distance</p>
                      <p className="text-xs font-semibold text-gray-700">
                        ₹{(estimatedFare.breakdown.baseFare + estimatedFare.breakdown.distanceFare).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white/50 rounded p-1.5 border border-primary-100">
                      <p className="text-[9px] text-gray-500 uppercase">Fees & Taxes</p>
                      <p className="text-xs font-semibold text-gray-700">
                        ₹{(estimatedFare.breakdown.totalFees + estimatedFare.breakdown.totalTaxes).toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end space-x-3 pt-2">
          <Link to="/trips" className="btn btn-outline">
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Creating Trip...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Trip
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTrip;
